import { useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import {
    useInboxData,
    GOOGLE_CLIENT_ID,
    ALLOWED_DOMAIN,
    IS_LOCAL,
    isAnswerable,
    getCachedMedia,
    fetchMedia,
    fetchThreadRows,
} from "@/inbox-data";

// Colour a lead by acquisition source: WhatsApp DM = green, Typeform = violet,
// PropGuru = red; anything else stays neutral. Matched loosely so casing/wording
// variants ("WhatsApp", "whatsapp dm", "PropGuru Lead"…) still map.
// Scroll the message with data-msg-id === id into view and flash it, for
// click-to-jump from a quoted reply to the original message.
function scrollToMessage(id) {
    if (id == null) return;
    const sel = (window.CSS && CSS.escape) ? CSS.escape(String(id)) : String(id);
    const el = document.querySelector(`[data-msg-id="${sel}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("wa-highlight");
    setTimeout(() => el.classList.remove("wa-highlight"), 1400);
}

function sourceColour(source) {
    const s = String(source || "").toLowerCase();
    if (!s) return null;
    if (/whats?app|\bwa\b|\bdm\b/.test(s)) return "#25d366";
    if (/type\s*form/.test(s)) return "#8b5cf6";
    if (/prop\s*guru/.test(s)) return "#ef4444";
    return null;
}

/* ============================================================
   AI Sales Bot Inbox — visual mockup
   ------------------------------------------------------------
   Pure-frontend reference design for Figment's sales inbox.
   • WhatsApp-Web-inspired layout (sidebar + thread + profile)
   • Three message authors: Prospect (left, neutral),
     Bot / AI (right, green), Human Staff (right, blue)
   • Light & dark themes via :root.light / :root.dark tokens
   • All colours come from wa-* Tailwind utilities — no hex
   ============================================================ */

/* ----------------------------- ICONS ------------------------- */
const Icon = {
    Search: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
        </svg>
    ),
    Person: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
        </svg>
    ),
    Robot: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <rect x="4" y="7" width="16" height="13" rx="3" />
            <path d="M12 3v4" />
            <circle cx="9" cy="13" r="1.2" fill="currentColor" />
            <circle cx="15" cy="13" r="1.2" fill="currentColor" />
            <path d="M9 17h6" />
        </svg>
    ),
    Headset: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
            <rect x="3" y="14" width="4" height="6" rx="1.5" />
            <rect x="17" y="14" width="4" height="6" rx="1.5" />
            <path d="M20 20a4 4 0 0 1-4 4h-1" />
        </svg>
    ),
    Sun: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
    ),
    Moon: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
    ),
    Send: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m3 11 18-8-8 18-2-8-8-2z" />
        </svg>
    ),
    Lead: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M5 12.5 10 17l9-10" />
        </svg>
    ),
    NoLead: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8" />
        </svg>
    ),
    Spark: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m13 2-3 7H4l5 4-2 7 6-4 6 4-2-7 5-4h-6z" />
        </svg>
    ),
    Info: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8h.01M11 12h1v5h1" />
        </svg>
    ),
    Close: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M6 6l12 12M18 6 6 18" />
        </svg>
    ),
    External: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M14 4h6v6" />
            <path d="M20 4 10 14" />
            <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
        </svg>
    ),
    Check2: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m2 13 4 4 8-8" />
            <path d="m10 13 4 4 8-8" />
        </svg>
    ),
    Plug: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M9 2v6M15 2v6" />
            <path d="M7 8h10v4a5 5 0 0 1-10 0z" />
            <path d="M12 17v5" />
        </svg>
    ),
    Image: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <rect x="3" y="4" width="18" height="16" rx="3" />
            <circle cx="8.5" cy="9.5" r="1.6" />
            <path d="m4 17 5-4.5 4 3.5 3-2.5 4 3.5" />
        </svg>
    ),
    Download: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M12 3v12" />
            <path d="m7 11 5 5 5-5" />
            <path d="M4 20h16" />
        </svg>
    ),
    Pencil: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
        </svg>
    ),
    Calendar: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
            <path d="M3 9h18M8 2.5v4M16 2.5v4" />
        </svg>
    ),
    Chevron: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m9 6 6 6-6 6" />
        </svg>
    ),
    Gear: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="12" r="3.2" />
            <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
        </svg>
    ),
};

// ── themed date picker (native <input type=date> popups can't be themed) ────────
const DP_WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const DP_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function ymd(d) {
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
}
function parseYmd(s) {
    if (!s) return null;
    const [y, m, d] = String(s).split("-").map(Number);
    return y ? new Date(y, m - 1, d) : null;
}
function DatePicker({ value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = parseYmd(value);
    const [view, setView] = useState(() => selected || new Date());
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const year = view.getFullYear();
    const month = view.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const isSel = (d) =>
        selected && selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === d;

    return (
        <div ref={ref} className="relative flex-1">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="wa-select-btn flex w-full items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-[12.5px]"
            >
                <span className={cx("truncate", !value && "text-wa-sub")}>{value || placeholder}</span>
                <Icon.Calendar width={14} height={14} className="shrink-0 text-wa-sub" />
            </button>
            {open && (
                <div
                    className="absolute left-0 z-40 mt-1.5 w-[250px] rounded-2xl p-2.5"
                    style={{ background: "var(--wa-panel)", border: "1px solid var(--wa-divider-strong)", boxShadow: "var(--wa-shadow)" }}
                >
                    <div className="mb-1.5 flex items-center justify-between px-1">
                        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} className="wa-btn flex h-7 w-7 items-center justify-center rounded-full text-wa-sub">
                            <Icon.Chevron width={14} height={14} style={{ transform: "rotate(180deg)" }} />
                        </button>
                        <span className="text-[12.5px] font-semibold text-wa-text-strong">{DP_MONTHS[month]} {year}</span>
                        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} className="wa-btn flex h-7 w-7 items-center justify-center rounded-full text-wa-sub">
                            <Icon.Chevron width={14} height={14} />
                        </button>
                    </div>
                    <div className="grid grid-cols-7 gap-0.5 text-center">
                        {DP_WEEKDAYS.map((w) => (
                            <div key={w} className="py-1 text-[10px] font-semibold uppercase text-wa-sub">{w}</div>
                        ))}
                        {cells.map((d, i) => d === null ? (
                            <div key={`b${i}`} />
                        ) : (
                            <button
                                key={d}
                                type="button"
                                onClick={() => { onChange(ymd(new Date(year, month, d))); setOpen(false); }}
                                className={cx("rounded-lg py-1.5 text-[12px]", isSel(d) ? "font-bold text-white" : "wa-btn text-wa-text")}
                                style={isSel(d) ? { background: "var(--wa-accent)" } : undefined}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                    {value && (
                        <button
                            type="button"
                            onClick={() => { onChange(""); setOpen(false); }}
                            className="wa-btn mt-2 w-full rounded-lg py-1.5 text-[11.5px] font-medium text-wa-sub"
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/* ----------------------------- HELPERS ----------------------- */
// Live data replaces the old mock STAFF/CONVERSATIONS/THREADS/PROFILE — see
// src/inbox-data.js (useInboxData). `assigned` on a conversation is the staff
// EMAIL (the live STAFF row id), so we resolve by email-keyed id.
const staffByIdIn = (staff, id) =>
    (staff || []).find((s) => s.id === (id == null ? id : String(id).toLowerCase())) || null;
const cx = (...xs) => xs.filter(Boolean).join(" ");

// Per-conversation deep link: hash routing (#/c/<phone>) so every conversation
// has a shareable URL. Mirrors the reference implementation.
function phoneFromHash() {
    if (typeof window === "undefined") return null;
    const m = (window.location.hash || "").match(/^#\/c\/(.+)$/);
    return m ? decodeURIComponent(m[1]) : null;
}

// Render plain message text with clickable links (WhatsApp-style). Catches both
// http(s):// URLs and bare www. links; everything else stays as text. A trailing
// sentence punctuation mark is kept out of the link so "…studio/." works.
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;
function linkify(text) {
    if (text == null) return text;
    return String(text).split(URL_RE).map((part, i) => {
        if (!/^(https?:\/\/|www\.)/i.test(part)) return part;
        // Trim trailing punctuation that isn't really part of the URL.
        const m = part.match(/^(.*?)([.,!?;:)]*)$/);
        const url = m ? m[1] : part;
        const tail = m ? m[2] : "";
        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        return (
            <span key={i}>
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline decoration-1 underline-offset-2 break-all hover:opacity-80"
                    style={{ color: "var(--wa-link)" }}
                >
                    {url}
                </a>
                {tail}
            </span>
        );
    });
}

/* ----------------------------- SUB-COMPONENTS ---------------- */

function Avatar({ initials, accent, size = 44, ring = false }) {
    return (
        <div
            className={cx(
                "flex items-center justify-center font-semibold select-none shrink-0",
                ring && "ring-2 ring-wa-accent ring-offset-2 ring-offset-wa-panel",
            )}
            style={{
                width: size,
                height: size,
                borderRadius: 999,
                background: `linear-gradient(135deg, ${accent}, ${accent}AA)`,
                color: "#fff",
                fontSize: Math.round(size * 0.36),
                letterSpacing: "-0.02em",
            }}
        >
            {initials}
        </div>
    );
}

function ZohoBadge({ active, asLink = false, href }) {
    if (active) {
        const inner = (
            <>
                <Icon.Lead width={11} height={11} />
                Lead
                <Icon.External width={10} height={10} className="opacity-80" />
            </>
        );
        const cls =
            "wa-btn inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase";
        const style = {
            background: "var(--wa-accent-soft)",
            color: "var(--wa-accent)",
        };
        if (asLink && href) {
            return (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="zoho-badge-redirect"
                    onClick={(e) => e.stopPropagation()}
                    className={cls}
                    style={style}
                    title="Open Zoho lead in a new tab"
                >
                    {inner}
                </a>
            );
        }
        return (
            <span
                data-testid="zoho-badge-active"
                className={cls}
                style={style}
                title="Linked to Zoho lead — open in Zoho"
            >
                {inner}
            </span>
        );
    }
    return (
        <span
            data-testid="zoho-badge-none"
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase text-wa-sub"
            style={{ background: "var(--wa-divider)" }}
            title="No Zoho lead linked"
        >
            <Icon.NoLead width={11} height={11} />
            No lead
        </span>
    );
}

function Switch({ on, onChange, label, testid, disabled = false }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={label}
            data-testid={testid}
            disabled={disabled}
            onClick={() => !disabled && onChange(!on)}
            className={cx(
                "wa-btn relative inline-flex items-center gap-2",
                disabled && "cursor-not-allowed opacity-40",
            )}
        >
            <span
                className="relative inline-block h-[22px] w-[38px] rounded-full transition-colors"
                style={{
                    background: on ? "var(--wa-accent)" : "var(--wa-divider-strong)",
                }}
            >
                <span
                    className="absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white shadow transition-all"
                    style={{ left: on ? 18 : 2 }}
                />
            </span>
        </button>
    );
}

// Fully theme-controlled dropdown — native <select> option popups can't be
// themed reliably (the OS/GTK draws them), so we render our own list using the
// wa-* tokens. Closes on outside-click or Escape. `options` is [{value,label}].
function Dropdown({ value, onChange, options, placeholder = "Select", testid, className, buttonClassName }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const selected = options.find((o) => o.value === value);
    return (
        <div ref={ref} className={cx("relative", className)}>
            <button
                type="button"
                data-testid={testid}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className={cx(
                    "wa-select-btn flex w-full items-center justify-between gap-2 rounded-full text-[13px] font-medium",
                    buttonClassName,
                )}
            >
                <span className="truncate">{selected ? selected.label : placeholder}</span>
                <svg
                    viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
                    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                    className={cx("shrink-0 transition-transform", open && "rotate-180")}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div
                    role="listbox"
                    className="wa-scroll absolute left-0 z-30 mt-1.5 max-h-72 w-max min-w-full max-w-[300px] overflow-y-auto rounded-2xl py-1.5"
                    style={{ background: "var(--wa-panel)", border: "1px solid var(--wa-divider-strong)", boxShadow: "var(--wa-shadow)" }}
                >
                    {options.map((o) => {
                        const isActive = o.value === value;
                        return (
                            <button
                                key={o.value === "" ? "__empty" : o.value}
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                onClick={() => { onChange(o.value); setOpen(false); }}
                                className="wa-option flex w-full items-center gap-2 px-3.5 py-2 text-left text-[13px]"
                                style={isActive ? { color: "var(--wa-accent)", fontWeight: 600 } : undefined}
                            >
                                <span className="flex-1 truncate">{o.label}</span>
                                {isActive && <Icon.Lead width={12} height={12} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ConversationRow({ c, selected, onClick, staffRoster }) {
    const staffRow = staffByIdIn(staffRoster, c.assigned);
    // Resolve a display object even if the assigned staff isn't in the roster.
    const staff = staffRow
        || (c.assigned ? { name: c.assignedName || c.assigned, colour: "var(--wa-accent)" } : null);
    return (
        <button
            type="button"
            data-testid={`conv-row-${c.id}`}
            onClick={onClick}
            className={cx(
                "wa-row group flex w-full items-center gap-3 px-4 py-3 text-left",
                "border-l-2",
                c.escalated
                    ? "is-escalated border-wa-danger"
                    : selected
                        ? "is-selected border-wa-accent"
                        : "border-transparent",
            )}
            style={
                c.escalated
                    ? { background: selected ? "var(--wa-danger-soft)" : undefined }
                    : undefined
            }
        >
            <Avatar initials={c.initials} accent={sourceColour(c.source) || c.accent} size={46} />

            <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                    <span
                        className={cx(
                            "truncate text-[15px] font-semibold",
                            c.escalated ? "text-wa-danger" : "text-wa-text-strong",
                        )}
                    >
                        {c.name || c.phone}
                    </span>
                    <span className={cx(
                        "shrink-0 text-[11px] tabular-nums",
                        c.escalated
                            ? "font-semibold text-wa-danger"
                            : c.unread > 0
                                ? "font-semibold text-wa-accent"
                                : "text-wa-sub",
                    )}>
                        {c.time}
                    </span>
                </div>

                <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-wa-sub">
                        {c.previewBy === "bot" && <Icon.Robot width={13} height={13} className="shrink-0" />}
                        {c.previewBy === "staff" && <Icon.Headset width={13} height={13} className="shrink-0" />}
                        <span className="truncate">{c.preview}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                        {c.unread > 0 && (
                            <span
                                className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold text-white"
                                style={{
                                    background: c.escalated
                                        ? "var(--wa-danger)"
                                        : "var(--wa-accent)",
                                }}
                            >
                                {c.unread}
                            </span>
                        )}
                    </span>
                </div>

                <div className="mt-1 flex items-center gap-1.5 text-[11px] text-wa-sub">
                    {c.escalated ? (
                        <span className="font-semibold uppercase tracking-wide" style={{ color: "var(--wa-danger)" }}>
                            Takeover
                        </span>
                    ) : c.zoho ? (
                        <span className="font-medium" style={{ color: "var(--wa-accent)" }}>Lead</span>
                    ) : null}
                    {/* INACTIVE marker. Only rendered for a KNOWN-inactive lead, which can
                        only be on screen while "Show inactive" is on — so it never badges a
                        list where every row is active anyway, and `null`/unknown is never
                        labelled. A red DOT (the agreed colour) with a muted label, rather
                        than red text: escalation already owns loud red on this row (left
                        border + red name + red unread pill) and must keep winning the eye.
                        Whether it was the 7-day rule or a human pin is in the tooltip,
                        not the label — these rows are already dense. */}
                    {c.activeLead === false && (
                        <>
                            {(c.escalated || c.zoho) && <span className="opacity-40">·</span>}
                            <span
                                data-testid="row-inactive"
                                className="inline-flex shrink-0 items-center gap-1"
                                title={c.activityIsManual
                                    ? "Marked inactive by hand"
                                    : "No inbound message in the last 7 days"}
                            >
                                <span
                                    className="inline-block h-[6px] w-[6px] rounded-full"
                                    style={{ background: "var(--wa-danger)" }}
                                />
                                Inactive
                            </span>
                        </>
                    )}
                    {(c.escalated || c.zoho || c.activeLead === false) && staff && <span className="opacity-40">·</span>}
                    <span className="truncate">{staff ? staff.name.split(" ")[0] : "Unassigned"}</span>
                </div>
            </div>
        </button>
    );
}

// Image message: shows a Load button (no auto-fetch). On click it calls the
// worker /media proxy ONCE via fetchMedia (session-cached), then renders the
// image. Because the resolved src is cached module-side, switching chats or the
// 5s poll never re-fetches — the image stays until the tab is closed/reloaded.
function ImageMessage({ m, onImageOpen }) {
    const ref = m.mediaRef;
    const [src, setSrc] = useState(() => getCachedMedia(ref));
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = () => {
        if (loading || src) return;
        setLoading(true);
        setErr("");
        fetchMedia(ref)
            .then(setSrc)
            .catch((e) => setErr(
                String(e && e.message) === "expired"
                    ? "Image expired (WhatsApp keeps media ~30 days)"
                    : "Couldn’t load image",
            ))
            .finally(() => setLoading(false));
    };

    if (src) {
        return (
            <button
                type="button"
                onClick={() => onImageOpen?.(src)}
                className={cx("block w-full", m.text && "mb-1.5")}
                title="View full screen"
            >
                <img
                    src={src}
                    alt={m.text || "Shared image"}
                    className="max-h-72 w-auto cursor-zoom-in rounded-2xl object-cover"
                    style={{ maxWidth: "100%", display: "block" }}
                    loading="lazy"
                />
            </button>
        );
    }

    return (
        <div
            className={cx("flex flex-col items-center justify-center gap-2 rounded-2xl px-6 py-6", m.text && "mb-1.5")}
            style={{ background: "var(--wa-panel3)", minWidth: 190 }}
        >
            <Icon.Image width={30} height={30} className="text-wa-sub" />
            <div className="text-[12px] font-medium text-wa-sub">Photo</div>
            <button
                type="button"
                data-testid="load-image"
                onClick={load}
                disabled={loading}
                className="wa-btn inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold disabled:opacity-60"
                style={{ background: "var(--wa-accent-soft)", color: "var(--wa-accent)" }}
            >
                {loading ? (
                    "Loading…"
                ) : (
                    <>
                        <Icon.Download width={13} height={13} />
                        Load image
                    </>
                )}
            </button>
            {err && (
                <button type="button" onClick={load} className="text-[11px] underline" style={{ color: "var(--wa-danger)" }}>
                    {err} · retry
                </button>
            )}
        </div>
    );
}

// Per-message staff note. Shows the existing note (+ who wrote it) with an edit
// pencil, or a faint "Add note" pencil when empty. Editing opens an inline textarea;
// Save writes conversations.notes/note_author via onSave (a Supabase PATCH).
function MessageNote({ note, noteBy, onSave, align }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(note || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    // Keep the draft in sync if the note changes underneath us (5s poll / another editor).
    useEffect(() => { if (!editing) setDraft(note || ""); }, [note, editing]);

    const save = () => {
        setSaving(true);
        setErr("");
        Promise.resolve(onSave(draft))
            .then(() => setEditing(false))
            .catch((e) => setErr(String(e?.message || "Couldn’t save note").slice(0, 90)))
            .finally(() => setSaving(false));
    };

    if (editing) {
        return (
            <div className={cx("mt-1.5 w-full max-w-[86%]", align === "right" ? "self-end" : "self-start")}>
                <div className="rounded-xl p-2" style={{ background: "var(--wa-panel)", border: "1px solid var(--wa-divider-strong)" }}>
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={2}
                        autoFocus
                        placeholder="Add a note for the team…"
                        className="wa-input w-full resize-none rounded-lg px-2.5 py-1.5 text-[13px]"
                    />
                    <div className="mt-1.5 flex items-center justify-end gap-1.5">
                        {err && <span className="mr-auto text-[11px]" style={{ color: "var(--wa-danger)" }}>{err}</span>}
                        <button
                            type="button"
                            onClick={() => { setEditing(false); setDraft(note || ""); setErr(""); }}
                            className="wa-btn rounded-full px-2.5 py-1 text-[11.5px] font-medium text-wa-sub"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            data-testid="note-save"
                            onClick={save}
                            disabled={saving}
                            className="wa-btn rounded-full px-3 py-1 text-[11.5px] font-semibold text-white disabled:opacity-50"
                            style={{ background: "var(--wa-accent)" }}
                        >
                            {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (note) {
        return (
            <div className={cx("mt-1.5 flex w-full max-w-[86%] items-start gap-1.5 rounded-xl px-2.5 py-1.5", align === "right" ? "self-end" : "self-start")}
                 style={{ background: "var(--wa-warn-soft)" }}>
                <Icon.Pencil width={11} height={11} className="mt-1 shrink-0" style={{ color: "var(--wa-warn)" }} />
                <div className="min-w-0 flex-1">
                    <div className="whitespace-pre-wrap break-words text-[12.5px]" style={{ color: "var(--wa-text-strong)" }}>{note}</div>
                    {noteBy && <div className="mt-0.5 text-[10.5px] text-wa-sub">— {noteBy}</div>}
                </div>
                <button type="button" data-testid="note-edit" onClick={() => setEditing(true)} title="Edit note"
                        className="wa-btn shrink-0 rounded-full p-1 text-wa-sub">
                    <Icon.Pencil width={12} height={12} />
                </button>
            </div>
        );
    }

    return (
        <button
            type="button"
            data-testid="note-add"
            onClick={() => setEditing(true)}
            title="Add note"
            className={cx("wa-btn mt-1 inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-[10.5px] text-wa-sub opacity-60 hover:opacity-100",
                align === "right" && "self-end")}
        >
            <Icon.Pencil width={11} height={11} /> Add note
        </button>
    );
}

function MessageBubble({ m, onImageOpen, onSaveNote }) {
    const isLeft = m.by === "prospect";
    // m.staff is the resolved staff display name (string) from the data layer.
    const staff = m.by === "staff" && m.staff ? { name: m.staff } : null;

    const bg =
        m.by === "prospect" ? "var(--wa-in)" :
        m.by === "bot"      ? "var(--wa-out)" :
        /* staff */            "var(--wa-staff)";

    /* Light theme keeps text dark in all bubbles; dark theme uses
       --wa-text-strong on the deeper-saturated bot / staff bubbles. */
    const textColor = "var(--wa-text-strong)";

    return (
        <div
            className={cx(
                "wa-fade flex w-full flex-col",
                isLeft ? "items-start" : "items-end",
            )}
        >
            <div
                data-msg-id={m.id}
                className={cx(
                    "relative max-w-[86%] rounded-3xl px-4 py-2.5 shadow-wa",
                )}
                style={{ background: bg, color: textColor }}
            >
                {m.by === "staff" && (
                    <div className="mb-0.5 text-[11.5px] font-semibold" style={{ opacity: 0.75 }}>
                        {staff?.name || "Staff"}
                    </div>
                )}

                {/* Quoted-reply snippet (WhatsApp "reply to"). Only rendered when the
                    data layer resolved the quoted message in this conversation
                    (m.quoted); otherwise the bubble is exactly as before — fail-open.
                    Click it to scroll to (and flash) the original message. */}
                {m.quoted && (
                    <button
                        type="button"
                        data-testid="quoted-reply"
                        onClick={() => scrollToMessage(m.quoted.id)}
                        title="Go to the quoted message"
                        className="mb-1.5 block w-full cursor-pointer overflow-hidden rounded-xl px-2.5 py-1.5 text-left transition hover:brightness-110"
                        style={{
                            background: "var(--wa-divider)",
                            borderLeft: `3px solid ${m.quoted.by === "prospect" ? "var(--wa-accent)" : "var(--wa-link)"}`,
                        }}
                    >
                        <div
                            className="text-[11px] font-semibold"
                            style={{ color: m.quoted.by === "prospect" ? "var(--wa-accent)" : "var(--wa-link)" }}
                        >
                            {m.quoted.by === "prospect" ? "Prospect" : m.quoted.by === "staff" ? (m.quoted.staff || "Staff") : "Figment AI"}
                        </div>
                        <div
                            className="text-[12.5px] leading-snug break-words"
                            style={{
                                opacity: 0.75,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {m.quoted.text}
                        </div>
                    </button>
                )}

                {/* Image messages load on demand via the worker /media proxy and
                    open full-screen in the lightbox once fetched. */}
                {m.format === "image" && <ImageMessage m={m} onImageOpen={onImageOpen} />}

                {m.text && (
                    <div className="text-[14.5px] leading-relaxed whitespace-pre-wrap break-words">{linkify(m.text)}</div>
                )}

                <div className="mt-1 flex items-center justify-end text-[10.5px] opacity-60">
                    <span className="tabular-nums">{m.time}</span>
                </div>
            </div>

            {/* Per-message staff note, shown just under the bubble on the same side. */}
            <MessageNote
                note={m.note}
                noteBy={m.noteBy}
                align={isLeft ? "left" : "right"}
                onSave={(text) => onSaveNote?.(m.id, text)}
            />
        </div>
    );
}

function DayDivider({ label }) {
    return (
        <div className="my-2 flex items-center justify-center">
            <span
                className="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-wa-sub shadow-wa"
                style={{ background: "var(--wa-panel)" }}
            >
                {label}
            </span>
        </div>
    );
}

// Full-screen image viewer. Click the backdrop, the ✕, or press Escape to close.
function Lightbox({ src, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    return (
        <div
            data-testid="lightbox"
            onClick={onClose}
            className="wa-fade fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.88)", backdropFilter: "blur(2px)" }}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="lightbox-close"
                className="wa-btn absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full text-white"
                style={{ background: "rgba(255,255,255,0.16)" }}
            >
                <Icon.Close width={22} height={22} />
            </button>
            <img
                src={src}
                alt="Full screen"
                onClick={(e) => e.stopPropagation()}
                className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain"
                style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}
            />
        </div>
    );
}

// Settings popup: manage the sales-staff roster — toggle active/inactive (which
// forces a reassign of that person's chats, never leaving them unassigned) and
// bulk-move all of one staff's chats to another.
function StaffManager({ staffAll, onClose, onBulkReassign, onReassignAll, onRedistribute, onSetActive }) {
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");
    const [deact, setDeact] = useState(null);          // staff pending deactivation (has chats)
    const [deactTarget, setDeactTarget] = useState("__rr");
    const [bulkFrom, setBulkFrom] = useState("");
    const [bulkTo, setBulkTo] = useState("");

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const active = staffAll.filter((s) => s.active);
    const withChats = staffAll.filter((s) => s.chatCount > 0);
    const asTarget = (s) => ({ id: s.id, email: s.email, name: s.name });

    const toggle = async (s) => {
        setErr("");
        if (s.active && s.chatCount > 0) { setDeact(s); setDeactTarget("__rr"); return; }
        setBusy(s.id);
        try { await onSetActive(s.id, !s.active); }
        catch (e) { setErr(String(e.message || e)); }
        finally { setBusy(""); }
    };

    const confirmDeact = async () => {
        if (!deact) return;
        const others = active.filter((s) => s.email && s.email !== deact.email);
        if (!others.length) { setErr("No other active staff to receive the chats — activate someone first."); return; }
        setBusy(deact.id); setErr("");
        try {
            if (deactTarget === "__rr") await onRedistribute(deact.email, others.map(asTarget));
            else {
                const t = others.find((s) => s.email === deactTarget);
                if (!t) throw new Error("Pick who receives the chats.");
                await onBulkReassign(deact.email, asTarget(t));
            }
            await onSetActive(deact.id, false);
            setDeact(null);
        } catch (e) { setErr(String(e.message || e)); }
        finally { setBusy(""); }
    };

    const doBulk = async () => {
        setErr("");
        const to = active.find((s) => s.email === bulkTo);
        if (!bulkFrom || !to) { setErr("Pick both a From and a To staff."); return; }
        setBusy("bulk");
        try {
            if (bulkFrom === "__all") {
                await onReassignAll(asTarget(to));
            } else {
                const from = withChats.find((s) => s.email === bulkFrom);
                if (!from) { setErr("Pick a valid From staff."); setBusy(""); return; }
                if (from.email === to.email) { setErr("From and To can't be the same person."); setBusy(""); return; }
                await onBulkReassign(from.email, asTarget(to));
            }
            setBulkFrom(""); setBulkTo("");
        } catch (e) { setErr(String(e.message || e)); }
        finally { setBusy(""); }
    };

    return (
        <div className="wa-fade fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)" }} onClick={onClose}>
            <div
                className="flex max-h-[88vh] w-[560px] max-w-full flex-col overflow-hidden rounded-2xl"
                style={{ background: "var(--wa-panel)", border: "1px solid var(--wa-divider-strong)", boxShadow: "var(--wa-shadow)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: "1px solid var(--wa-divider)" }}>
                    <div className="text-[15px] font-bold text-wa-text-strong">Settings · Manage staff</div>
                    <button type="button" onClick={onClose} aria-label="Close" className="wa-btn flex h-9 w-9 items-center justify-center rounded-full text-wa-sub">
                        <Icon.Close width={16} height={16} />
                    </button>
                </header>

                <div className="wa-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">
                    {err && (
                        <div className="mb-3 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: "var(--wa-danger-soft)", color: "var(--wa-danger)" }}>{err}</div>
                    )}

                    <div className="space-y-1.5">
                        {staffAll.length === 0 && <div className="py-6 text-center text-[13px] text-wa-sub">No staff found.</div>}
                        {staffAll.map((s) => (
                            <div key={s.id} className="rounded-xl" style={{ background: "var(--wa-panel2)" }}>
                                <div className="flex items-center gap-3 px-3 py-2.5">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-[13.5px] font-semibold text-wa-text-strong">{s.name}</div>
                                        <div className="truncate text-[11.5px] text-wa-sub">{s.email || "—"} · {s.chatCount} chat{s.chatCount === 1 ? "" : "s"}</div>
                                    </div>
                                    <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: s.active ? "var(--wa-accent)" : "var(--wa-sub)" }}>
                                        {s.active ? "Active" : "Inactive"}
                                    </span>
                                    <Switch on={s.active} onChange={() => toggle(s)} label={`Toggle ${s.name}`} disabled={busy === s.id} />
                                </div>
                                {deact && deact.id === s.id && (
                                    <div className="px-3 py-2.5" style={{ borderTop: "1px solid var(--wa-divider)" }}>
                                        <div className="mb-2 text-[12px] text-wa-sub">
                                            Deactivating — move <b className="text-wa-text-strong">{s.chatCount}</b> chat{s.chatCount === 1 ? "" : "s"} to:
                                        </div>
                                        <Dropdown
                                            testid="deact-target"
                                            buttonClassName="px-3 py-2"
                                            value={deactTarget}
                                            onChange={setDeactTarget}
                                            options={[
                                                { value: "__rr", label: "Distribute evenly across active staff" },
                                                ...active.filter((a) => a.email && a.email !== s.email).map((a) => ({ value: a.email, label: `Assign all to ${a.name}` })),
                                            ]}
                                        />
                                        <div className="mt-2 flex justify-end gap-1.5">
                                            <button type="button" onClick={() => setDeact(null)} className="wa-btn rounded-full px-3 py-1.5 text-[12px] font-medium text-wa-sub">Cancel</button>
                                            <button type="button" onClick={confirmDeact} disabled={busy === s.id} className="wa-btn rounded-full px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50" style={{ background: "var(--wa-danger)" }}>
                                                {busy === s.id ? "Working…" : "Deactivate & reassign"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-5">
                        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-wa-sub">Bulk reassign</div>
                        <div className="space-y-2 rounded-xl p-3" style={{ background: "var(--wa-panel2)" }}>
                            <div className="flex items-center gap-2">
                                <Dropdown testid="bulk-from" className="flex-1" buttonClassName="px-3 py-2" value={bulkFrom} onChange={setBulkFrom}
                                    options={[
                                        { value: "", label: "From…" },
                                        { value: "__all", label: "All conversations" },
                                        ...withChats.map((s) => ({ value: s.email, label: `${s.name} (${s.chatCount})` })),
                                    ]} />
                                <Icon.Chevron width={16} height={16} className="shrink-0 text-wa-sub" />
                                <Dropdown testid="bulk-to" className="flex-1" buttonClassName="px-3 py-2" value={bulkTo} onChange={setBulkTo}
                                    options={[{ value: "", label: "To…" }, ...active.map((s) => ({ value: s.email, label: s.name }))]} />
                            </div>
                            <button type="button" onClick={doBulk} disabled={busy === "bulk" || !bulkFrom || !bulkTo}
                                className="wa-btn w-full rounded-full py-2 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
                                style={{ background: "var(--wa-accent)" }}>
                                {busy === "bulk" ? "Reassigning…" : "Reassign all chats"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ----------------------------- MAIN APP ---------------------- */

function Inbox({ signedInEmail }) {
    /* ----- Live data layer (Supabase poll every 5s) ----- */
    const { data, threads: FULL_THREADS, loadThread, error, toggleEscalation, setLeadActivity, reassign: reassignStaff, sendReply, saveNote, markSeen, bulkReassign, reassignAll, redistribute, setActive } = useInboxData();
    const CONVERSATIONS = data.conversations;
    const THREADS = FULL_THREADS; // per-conversation full history (fetched on open); sidebar poll no longer carries bodies
    const PROFILE = data.profiles;
    const STAFF = data.staff;
    const STAFF_ALL = data.staffAll || [];
    const [showStaff, setShowStaff] = useState(false); // Settings → Manage staff popup

    /* ----- Theme (persisted, defaults to dark) ----- */
    const [theme, setTheme] = useState(() => {
        if (typeof window === "undefined") return "dark";
        return localStorage.getItem("wa-theme") || "dark";
    });
    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("dark", "light");
        root.classList.add(theme);
        localStorage.setItem("wa-theme", theme);
    }, [theme]);

    /* ----- Inbox state ----- */
    const [search, setSearch] = useState("");
    const [filterStaff, setFilterStaff] = useState("all");
    // LEAD ACTIVITY toggle (`+leadactive`). A lead is ACTIVE when they sent us a
    // genuine inbound in the last 7 days, or a human pinned them active in the
    // profile panel (users.last_inbound_at + users.activity_override, both kept
    // current by the db/72 trigger — a reply from a dormant lead flips them back
    // and clears the pin DB-side, so there is no client state to reconcile).
    // OFF (the default) hides inactive leads; ON reveals them alongside the active
    // ones. There is deliberately no inactive-ONLY state: this is a "show more"
    // reveal, not a two-way bucket swap. Applies LIVE like the staff filter (it is
    // a toggle, not a form field), and Clear leaves it alone — same as staff.
    const [showInactive, setShowInactive] = useState(false);
    // Pending (what the controls edit) vs applied (what actually filters the list).
    // Source + date only take effect when "Apply filters" is clicked — cheaper than
    // re-filtering on every pick, and predictable.
    const [filterSource, setFilterSource] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [appliedSource, setAppliedSource] = useState("all");
    const [appliedFrom, setAppliedFrom] = useState("");
    const [appliedTo, setAppliedTo] = useState("");
    const [exporting, setExporting] = useState(false);
    const [activeId, setActiveId] = useState(() => phoneFromHash());
    const [showProfile, setShowProfile] = useState(true);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const replyRef = useRef(null);
    const [lightbox, setLightbox] = useState(null); // image src open full-screen, or null
    const [savingActivity, setSavingActivity] = useState(false); // manual activity pin write in flight

    // Deep-link (#/c/<phone>) ↔ selected conversation, like the reference.
    useEffect(() => {
        const want = activeId ? `#/c/${encodeURIComponent(activeId)}` : "";
        if (activeId && window.location.hash !== want) {
            window.location.hash = want;
        }
    }, [activeId]);
    useEffect(() => {
        const onHash = () => setActiveId(phoneFromHash());
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    // Distinct source values present in the data → the source-filter dropdown.
    const SOURCES = useMemo(() => {
        const set = new Set();
        for (const c of CONVERSATIONS) if (c.source) set.add(c.source);
        return [...set].sort();
    }, [CONVERSATIONS]);

    // APPLIED date range → [ms]. `to` is pushed to end-of-day so it's inclusive.
    const fromMs = appliedFrom ? Date.parse(appliedFrom) : null;
    const toMs = appliedTo ? Date.parse(appliedTo) + 86399999 : null;

    // Everything EXCEPT the activity toggle. Kept separate so the count on the toggle
    // describes the CURRENT selection (search/staff/source/date already applied) —
    // i.e. "of what you're looking at, N more are inactive" — rather than the whole DB.
    const filteredBase = useMemo(() => {
        const q = search.trim().toLowerCase();
        return CONVERSATIONS.filter((c) => {
            if (filterStaff === "unassigned" && c.assigned) return false;
            if (filterStaff !== "all" && filterStaff !== "unassigned"
                && (c.assigned || "").toLowerCase() !== filterStaff) return false;
            if (appliedSource !== "all" && (c.source || "") !== appliedSource) return false;
            if (fromMs != null || toMs != null) {
                const t = c.inboundAt || c.lastAt || 0; // "came-in" date (users.created_at)
                if (!t) return false;
                if (fromMs != null && t < fromMs) return false;
                if (toMs != null && t > toMs) return false;
            }
            if (!q) return true;
            return (
                (c.name || "").toLowerCase().includes(q) ||
                (c.phone || "").toLowerCase().includes(q) ||
                (c.preview || "").toLowerCase().includes(q)
            );
        });
    }, [CONVERSATIONS, search, filterStaff, appliedSource, fromMs, toMs]);

    // How many of the current selection are inactive — i.e. exactly how many rows
    // the toggle is hiding / would reveal. Shown on the toggle so the archive is
    // never silently invisible.
    const inactiveCount = useMemo(
        () => filteredBase.reduce((n, c) => n + (c.activeLead === false ? 1 : 0), 0),
        [filteredBase],
    );

    // ONLY `activeLead === false` is hidden. `true` shows, and so does `null`, which
    // means UNKNOWN (the fallback fetch that omits users.last_inbound_at, e.g. before
    // db/72 is run). Absent must never be read as inactive, or the whole inbox would
    // vanish on a DB that predates the column.
    const filtered = useMemo(
        () => (showInactive ? filteredBase : filteredBase.filter((c) => c.activeLead !== false)),
        [filteredBase, showInactive],
    );

    // Apply/clear the source + date filters (search + staff stay live).
    const filtersDirty = filterSource !== appliedSource || dateFrom !== appliedFrom || dateTo !== appliedTo;
    const filtersActive = appliedSource !== "all" || !!appliedFrom || !!appliedTo;
    const applyFilters = () => {
        setAppliedSource(filterSource);
        setAppliedFrom(dateFrom);
        setAppliedTo(dateTo);
    };
    const clearFilters = () => {
        setFilterSource("all"); setDateFrom(""); setDateTo("");
        setAppliedSource("all"); setAppliedFrom(""); setAppliedTo("");
    };

    // Sidebar pagination — 100 conversations per page with Prev/Next, so a long
    // prospect list is browsable in chunks. Resets to the first page whenever the
    // search/filter changes; clamps if the filtered list shrinks below the page.
    const PAGE_SIZE = 100;
    const [page, setPage] = useState(0);
    useEffect(() => { setPage(0); }, [search, filterStaff, showInactive, appliedSource, appliedFrom, appliedTo]);
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const paged = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    // Nothing is auto-selected: on load (or a fresh refresh) the thread pane shows
    // a "pick a conversation" placeholder until the user clicks a row. A stale
    // deep-link that no longer matches any conversation resolves to null too.
    const active = useMemo(
        () => CONVERSATIONS.find((c) => c.id === activeId) || null,
        [CONVERSATIONS, activeId],
    );
    const thread = (active && THREADS[active.id]) || [];
    const profile = (active && PROFILE[active.id]) || null;

    // Fetch the OPEN conversation's FULL history on select, and re-poll it every 5s while open
    // (so live incoming/human-takeover messages appear). One conversation is tiny — negligible egress.
    useEffect(() => {
        if (!activeId) return;
        loadThread(activeId);
        const id = setInterval(() => loadThread(activeId), 5000);
        return () => clearInterval(id);
    }, [activeId, loadThread]);

    // Viewing a conversation marks it read up to its latest message — clears the
    // unread badge, and keeps it clear as new messages arrive while it's open.
    const activePhone = active?.phone;
    const activeLastAt = active?.lastAt;
    useEffect(() => {
        if (activePhone) markSeen(activePhone, activeLastAt);
    }, [activePhone, activeLastAt, markSeen]);

    // Human takeover + assigned staff are derived from the live record.
    const humanTakeover = Boolean(active && active.escalated);
    const assignedStaff = active ? staffByIdIn(STAFF, active.assigned) : null;
    const reassign = assignedStaff ? assignedStaff.id : "";

    // Test-mode guard: in local mode only whitelisted numbers may be acted on.
    const answerable = active ? isAnswerable(active.phone) : false;
    const blockedByTestMode = IS_LOCAL && active && !answerable;

    const onToggleTakeover = () => {
        if (!active || blockedByTestMode) return;
        toggleEscalation(active.phone, active.escalated).catch(() => {});
    };
    const onReassign = (id) => {
        if (!active) return;
        const s = id ? staffByIdIn(STAFF, id) : null;
        reassignStaff(active.phone, s).catch(() => {});
    };
    const onSend = (e) => {
        e.preventDefault();
        const text = draft.trim();
        if (!active || !text || sending) return;
        setSending(true);
        sendReply(active.phone, text, signedInEmail)
            .then(() => setDraft(""))
            .catch(() => {})
            .finally(() => setSending(false));
    };

    // Export the CURRENTLY-FILTERED conversations (or all, if no filters) to a JSON
    // file — full transcripts + lead profile + notes. Fetches each conversation's
    // history (concurrency-capped) so it doesn't hammer Supabase; shows progress.
    const onExport = async () => {
        if (exporting) return;
        const list = filtered;
        if (!list.length) return;
        setExporting(true);
        try {
            const byWhom = (m) =>
                (m.sender === "staff" || (m.staff_email && String(m.staff_email).trim())) ? "staff"
                : (m.sender === "prospect" || m.is_user_message) ? "prospect" : "bot";
            const out = [];
            const CONC = 5;
            for (let i = 0; i < list.length; i += CONC) {
                const batch = list.slice(i, i + CONC);
                const done = await Promise.all(batch.map(async (c) => {
                    const rows = await fetchThreadRows(c.phone).catch(() => []);
                    const p = PROFILE[c.id] || {};
                    return {
                        phone: c.phone,
                        name: c.name || null,
                        source: c.source || null,
                        email: p.email || null,
                        budget: p.budget || null,
                        area: p.area || null,
                        moveIn: p.moveIn || null,
                        occupancy: p.occupancy || null,
                        stay: p.stay || null,
                        zohoLead: p.zohoId || null,
                        assignedStaff: c.assignedName || c.assigned || null,
                        messages: rows.map((m) => ({
                            by: byWhom(m),
                            staff: m.staff_email || null,
                            text: m.format === "image" ? null : (m.message_body || ""),
                            format: m.format || "text",
                            mediaId: m.format === "image" ? m.message_body : null,
                            time: m.created_at,
                            note: m.notes || null,
                            noteBy: m.note_author || null,
                        })),
                    };
                }));
                out.push(...done);
            }
            const payload = {
                exported_at: new Date().toISOString(),
                filters: {
                    source: filterSource, from: dateFrom || null, to: dateTo || null,
                    staff: filterStaff, search: search || null,
                    activity: showInactive ? "active+inactive" : "active only",
                },
                count: out.length,
                conversations: out,
            };
            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `figment-inbox-export-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };
    // Auto-grow the reply textarea with its content, up to ~half the chat area
    // (50% of the viewport height), then it scrolls. Snaps back once sent/cleared.
    useEffect(() => {
        const el = replyRef.current;
        if (!el) return;
        el.style.height = "auto";
        const cap = Math.round(window.innerHeight * 0.5);
        el.style.height = `${Math.min(el.scrollHeight, cap)}px`;
    }, [draft]);

    // Full-screen state only while there's genuinely nothing to show yet (initial
    // load or a load error). Once conversations exist we render the normal layout
    // — sidebar included — so the user can pick a thread even with none selected.
    if (!CONVERSATIONS.length) {
        return (
            <div
                data-testid="inbox-root"
                className="flex h-screen w-full items-center justify-center bg-wa-bg text-wa-sub"
            >
                <div className="text-center">
                    <div className="text-[14px] font-semibold text-wa-text-strong">
                        {error ? "Couldn’t load conversations" : "Loading conversations…"}
                    </div>
                    {error && <div className="mt-1 max-w-md px-6 text-[12.5px] text-wa-sub">{error}</div>}
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="inbox-root"
            className="flex h-screen w-full overflow-hidden bg-wa-bg text-wa-text"
        >
            {/* =========================================================
                SIDEBAR
            ========================================================= */}
            <aside
                data-testid="sidebar"
                className="flex h-full w-[360px] shrink-0 flex-col border-r"
                style={{ background: "var(--wa-panel)", borderColor: "var(--wa-divider)" }}
            >
                {/* Header */}
                <header
                    className="flex items-center justify-between px-4 py-3.5"
                    style={{ borderBottom: "1px solid var(--wa-divider)" }}
                >
                    <div className="flex items-center gap-2">
                        <div className="text-[16px] font-bold text-wa-text-strong tracking-tight">
                            Figment Inbox
                        </div>
                        {IS_LOCAL && (
                            <span
                                data-testid="testmode-chip"
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-[3px] text-[9.5px] font-bold uppercase tracking-wider"
                                style={{
                                    background: "var(--wa-warn-soft)",
                                    color: "var(--wa-warn)",
                                    border: "1px solid var(--wa-warn-soft)",
                                }}
                                title="Local test mode — only whitelisted numbers can be escalated or replied to"
                            >
                                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--wa-warn)" }} />
                                Test mode
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            data-testid="settings-btn"
                            onClick={() => setShowStaff(true)}
                            className="wa-btn flex h-9 w-9 items-center justify-center rounded-full text-wa-sub"
                            style={{ background: "var(--wa-panel2)" }}
                            title="Settings — manage staff"
                            aria-label="Settings"
                        >
                            <Icon.Gear width={17} height={17} />
                        </button>
                        <button
                            type="button"
                            data-testid="signed-in-chip"
                            className="wa-btn hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium text-wa-sub md:inline-flex"
                            style={{ border: "1px solid var(--wa-divider-strong)" }}
                            title="Signed in"
                        >
                            <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--wa-accent)" }} />
                            {signedInEmail || "signed in"}
                        </button>
                    </div>
                </header>

                {/* Search + filter + theme */}
                <div className="space-y-2.5 px-4 pb-3 pt-3">
                    <label className="relative block">
                        <span className="sr-only">Search conversations</span>
                        <Icon.Search
                            width={15}
                            height={15}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-wa-sub"
                        />
                        <input
                            data-testid="search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, number or message"
                            className="wa-input w-full rounded-xl py-2.5 pl-9 pr-3 text-[13px]"
                        />
                    </label>

                    <div className="flex items-center gap-2">
                        <Dropdown
                            testid="filter-staff"
                            className="flex-1"
                            buttonClassName="px-3.5 py-2.5"
                            value={filterStaff}
                            onChange={setFilterStaff}
                            options={[
                                { value: "all", label: "All conversations" },
                                { value: "unassigned", label: "Unassigned" },
                                ...STAFF.map((s) => ({ value: s.id, label: `Assigned: ${s.name}` })),
                            ]}
                        />

                        <button
                            type="button"
                            data-testid="theme-toggle"
                            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                            className="wa-btn flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full"
                            style={{ background: "var(--wa-panel2)", color: "var(--wa-text)" }}
                            title={theme === "dark" ? "Switch to light" : "Switch to dark"}
                            aria-label="Toggle theme"
                        >
                            {theme === "dark" ? <Icon.Sun width={17} height={17} /> : <Icon.Moon width={17} height={17} />}
                        </button>
                    </div>

                    {/* Lead activity — a "show inactive too" reveal, not a bucket swap.
                        OFF (default) = active leads only; ON = active + inactive.
                        Live (no Apply): it's a toggle, not a form field. */}
                    <button
                        type="button"
                        data-testid="filter-activity"
                        role="switch"
                        aria-checked={showInactive}
                        onClick={() => setShowInactive((v) => !v)}
                        title={showInactive
                            ? "Hide leads with no inbound message in the last 7 days"
                            : "Also show leads with no inbound message in the last 7 days"}
                        className="wa-btn flex w-full items-center gap-2.5 rounded-full py-2 pl-2 pr-3.5 text-[12.5px] font-semibold"
                        style={showInactive
                            ? { background: "var(--wa-accent-soft)", color: "var(--wa-accent)" }
                            : { background: "var(--wa-panel2)" }}
                    >
                        {/* Track + knob — reads as a switch in both themes. */}
                        <span
                            aria-hidden="true"
                            className="relative inline-block h-[18px] w-[32px] shrink-0 rounded-full transition-colors"
                            style={{ background: showInactive ? "var(--wa-accent)" : "var(--wa-divider-strong)" }}
                        >
                            <span
                                className="absolute top-[3px] h-3 w-3 rounded-full transition-all"
                                style={{
                                    left: showInactive ? "17px" : "3px",
                                    background: showInactive ? "#fff" : "var(--wa-sub)",
                                }}
                            />
                        </span>
                        <span className={showInactive ? undefined : "text-wa-sub"}>Show inactive</span>
                        <span
                            className="ml-auto shrink-0 tabular-nums text-[11.5px] font-bold opacity-70"
                            data-testid="filter-activity-count"
                        >
                            {inactiveCount}
                        </span>
                    </button>

                    {/* Source filter + JSON export */}
                    <div className="flex items-center gap-2">
                        <Dropdown
                            testid="filter-source"
                            className="flex-1"
                            buttonClassName="px-3.5 py-2.5"
                            value={filterSource}
                            onChange={setFilterSource}
                            options={[
                                { value: "all", label: "All sources" },
                                ...SOURCES.map((s) => ({ value: s, label: s })),
                            ]}
                        />
                        <button
                            type="button"
                            data-testid="export-json"
                            onClick={onExport}
                            disabled={exporting || filtered.length === 0}
                            title="Export the filtered conversations as JSON (full transcripts)"
                            className="wa-btn flex h-[42px] shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ background: "var(--wa-accent-soft)", color: "var(--wa-accent)" }}
                        >
                            <Icon.Download width={15} height={15} />
                            {exporting ? "Exporting…" : `Export (${filtered.length})`}
                        </button>
                    </div>

                    {/* Date range (by inbound / came-in date) — themed pickers */}
                    <div className="flex items-center gap-2">
                        <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" />
                        <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" />
                    </div>

                    {/* Apply / clear — source + date only take effect on Apply */}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid="apply-filters"
                            onClick={applyFilters}
                            disabled={!filtersDirty}
                            className="wa-btn flex-1 rounded-full py-2 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
                            style={{ background: "var(--wa-accent)" }}
                        >
                            {filtersDirty ? "Apply filters" : "Filters applied"}
                        </button>
                        <button
                            type="button"
                            data-testid="clear-filters"
                            onClick={clearFilters}
                            disabled={!filtersActive && !filtersDirty}
                            className="wa-btn rounded-full px-4 py-2 text-[12.5px] font-semibold text-wa-sub disabled:cursor-not-allowed disabled:opacity-45"
                            style={{ background: "var(--wa-panel2)" }}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Conversation list */}
                <div
                    className="wa-scroll min-h-0 flex-1 overflow-y-auto"
                    data-testid="conv-list"
                    style={{ borderTop: "1px solid var(--wa-divider)" }}
                >
                    {filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                            <div
                                className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl"
                                style={{ background: "var(--wa-panel2)", color: "var(--wa-sub)" }}
                            >
                                <Icon.Search width={20} height={20} />
                            </div>
                            <div className="text-[14px] font-semibold text-wa-text-strong">
                                No conversations match
                            </div>
                            <div className="mt-1 text-[12.5px] text-wa-sub">
                                Try a different name or clear the filter.
                            </div>
                        </div>
                    ) : (
                        paged.map((c) => (
                            <ConversationRow
                                key={c.id}
                                c={c}
                                staffRoster={STAFF}
                                selected={active && c.id === active.id}
                                onClick={() => setActiveId(c.id)}
                            />
                        ))
                    )}
                </div>

                {/* Pager — only when there's more than one page of conversations. */}
                {filtered.length > PAGE_SIZE && (
                    <div
                        data-testid="conv-pager"
                        className="flex items-center justify-between gap-2 px-4 py-2.5"
                        style={{ borderTop: "1px solid var(--wa-divider)", background: "var(--wa-panel)" }}
                    >
                        <button
                            type="button"
                            data-testid="pager-prev"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            className="wa-btn rounded-full px-3 py-1 text-[12px] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
                            style={{ background: "var(--wa-panel2)", color: "var(--wa-text)" }}
                        >
                            Prev
                        </button>
                        <span className="text-[11.5px] tabular-nums text-wa-sub">
                            {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
                        </span>
                        <button
                            type="button"
                            data-testid="pager-next"
                            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                            disabled={safePage >= pageCount - 1}
                            className="wa-btn rounded-full px-3 py-1 text-[12px] font-semibold disabled:cursor-not-allowed disabled:opacity-40"
                            style={{ background: "var(--wa-panel2)", color: "var(--wa-text)" }}
                        >
                            Next
                        </button>
                    </div>
                )}
            </aside>

            {active ? (
              <>
            {/* =========================================================
                CHAT THREAD
            ========================================================= */}
            <main
                data-testid="chat-area"
                className="flex h-full min-w-0 flex-1 flex-col"
                style={{ background: "var(--wa-bg)" }}
            >
                {/* Chat header */}
                <header
                    className="flex items-center justify-between gap-3 px-5 py-3"
                    style={{
                        background: "var(--wa-panel)",
                        borderBottom: "1px solid var(--wa-divider)",
                    }}
                >
                    <div className="flex min-w-0 items-center gap-3">
                        <Avatar initials={active.initials} accent={sourceColour(active.source) || active.accent} size={42} />
                        <div className="min-w-0 leading-tight">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="truncate text-[15px] font-bold text-wa-text-strong">
                                    {active.name || active.phone}
                                </span>
                            </div>
                            <div className="mt-0.5 flex items-center gap-2 text-[12px] text-wa-sub">
                                <span className="tabular-nums">{active.phone}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {active.zoho && !showProfile && profile?.zohoId && (
                            <a
                                href={profile.zohoId}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="zoho-redirect-btn"
                                className="wa-btn hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold md:inline-flex"
                                style={{
                                    background: "var(--wa-accent-soft)",
                                    color: "var(--wa-accent)",
                                    border: "1px solid var(--wa-accent-soft)",
                                }}
                                title="Open this prospect's Zoho lead in a new tab"
                            >
                                <Icon.Lead width={13} height={13} />
                                Open in Zoho
                                <Icon.External width={12} height={12} />
                            </a>
                        )}

                        <div className="hidden items-center gap-2 md:flex">
                            <span className="text-[11.5px] font-medium uppercase tracking-wide text-wa-sub">
                                Assigned
                            </span>
                            <Dropdown
                                testid="reassign-staff"
                                buttonClassName="px-4 py-1.5"
                                value={reassign}
                                onChange={onReassign}
                                options={[
                                    { value: "", label: "— Unassigned —" },
                                    ...STAFF.map((s) => ({ value: s.id, label: s.name })),
                                ]}
                            />
                        </div>

                        <div
                            className="hidden items-center gap-2 rounded-full px-3 py-1.5 md:flex"
                            style={{ background: "var(--wa-panel2)" }}
                        >
                            <Icon.Headset width={14} height={14} className="text-wa-sub" />
                            <span className="text-[12px] font-medium text-wa-text">Human takeover</span>
                            <Switch
                                on={humanTakeover}
                                onChange={onToggleTakeover}
                                label="Human takeover"
                                testid="human-takeover-switch"
                                disabled={blockedByTestMode}
                            />
                        </div>

                        <button
                            type="button"
                            data-testid="toggle-profile"
                            onClick={() => setShowProfile((s) => !s)}
                            className="wa-btn flex h-10 w-10 items-center justify-center rounded-full text-wa-sub"
                            style={{ background: "var(--wa-panel2)" }}
                            title={showProfile ? "Hide profile" : "Show profile"}
                            aria-label="Toggle profile panel"
                        >
                            <Icon.Info width={17} height={17} />
                        </button>
                    </div>
                </header>

                {/* Banner: takeover state */}
                {humanTakeover && (
                    <div
                        data-testid="takeover-banner"
                        className="flex items-center gap-2 px-5 py-2 text-[12px] font-medium"
                        style={{
                            background: "var(--wa-danger-soft)",
                            color: "var(--wa-danger)",
                            borderBottom: "1px solid var(--wa-divider)",
                        }}
                    >
                        <Icon.Spark width={12} height={12} />
                        Human takeover is on — the AI will pause and your replies are relayed live.
                    </div>
                )}

                {/* Banner: test-mode block (local env, non-whitelisted number) */}
                {blockedByTestMode && (
                    <div
                        data-testid="testmode-banner"
                        className="flex items-center gap-2 px-5 py-2 text-[12px] font-medium"
                        style={{
                            background: "var(--wa-warn-soft)",
                            color: "var(--wa-warn)",
                            borderBottom: "1px solid var(--wa-divider)",
                        }}
                    >
                        Test mode — this number isn’t whitelisted. Escalation and replies are disabled here so live chats stay untouched. View only.
                    </div>
                )}

                {/* Messages */}
                <div className="wa-wallpaper wa-scroll min-h-0 flex-1 overflow-y-auto">
                    <div className="mx-auto flex max-w-5xl flex-col gap-3 px-5 py-6">
                        {thread.map((group, gi) => (
                            <section key={gi} className="flex flex-col gap-2.5">
                                <DayDivider label={group.day} />
                                {group.items.map((m) => (
                                    <MessageBubble
                                        key={m.id}
                                        m={m}
                                        onImageOpen={setLightbox}
                                        onSaveNote={(id, text) => saveNote(id, text, signedInEmail)}
                                    />
                                ))}
                            </section>
                        ))}
                    </div>
                </div>

                {/* Reply box */}
                <footer
                    className="px-5 py-3"
                    style={{
                        background: "var(--wa-panel)",
                        borderTop: "1px solid var(--wa-divider)",
                    }}
                >
                    <form
                        data-testid="reply-form"
                        onSubmit={onSend}
                        className="mx-auto flex max-w-5xl items-end gap-2"
                    >
                        <div className="flex flex-1 items-end gap-2 rounded-3xl px-4 py-2"
                             style={{ background: "var(--wa-panel2)", border: "1px solid var(--wa-divider)" }}>
                            <span
                                className="mb-1 inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide"
                                style={{
                                    background: humanTakeover ? "rgba(29, 78, 137, 0.18)" : "var(--wa-accent-soft)",
                                    color: humanTakeover ? "#5fa8ff" : "var(--wa-accent)",
                                }}
                            >
                                {humanTakeover ? <Icon.Headset width={11} height={11} /> : <Icon.Robot width={11} height={11} />}
                                {humanTakeover
                                    ? `Reply as ${(signedInEmail || "you").split("@")[0]}`
                                    : "AI is replying"}
                            </span>
                            <textarea
                                ref={replyRef}
                                data-testid="reply-input"
                                rows={1}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                placeholder={blockedByTestMode ? "Test mode — replies disabled for non-whitelisted numbers" : humanTakeover ? "Type a message to relay…" : "Turn on takeover to reply manually"}
                                disabled={!humanTakeover || sending || blockedByTestMode}
                                className="wa-scroll wa-input flex-1 resize-none rounded-md border-0 bg-transparent px-1 py-1.5 text-[14px] leading-relaxed disabled:opacity-60"
                                style={{ background: "transparent", maxHeight: "50vh" }}
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="reply-send"
                            disabled={!humanTakeover || sending || blockedByTestMode || draft.trim().length === 0}
                            className="wa-btn flex h-11 w-11 items-center justify-center rounded-full text-white disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ background: "var(--wa-accent)" }}
                            aria-label="Send reply"
                        >
                            <Icon.Send width={17} height={17} />
                        </button>
                    </form>
                </footer>
            </main>

            {/* =========================================================
                PROFILE PANEL
            ========================================================= */}
            {showProfile && (
                <aside
                    data-testid="profile-panel"
                    className="hidden h-full w-[340px] shrink-0 flex-col lg:flex"
                    style={{
                        background: "var(--wa-panel)",
                        borderLeft: "1px solid var(--wa-divider)",
                    }}
                >
                    <header
                        className="flex items-center justify-between px-5 py-3.5"
                        style={{ borderBottom: "1px solid var(--wa-divider)" }}
                    >
                        <div className="text-[13px] font-semibold uppercase tracking-wide text-wa-sub">
                            Prospect profile
                        </div>
                        <button
                            type="button"
                            data-testid="close-profile"
                            onClick={() => setShowProfile(false)}
                            className="wa-btn flex h-9 w-9 items-center justify-center rounded-full text-wa-sub"
                            aria-label="Close profile"
                        >
                            <Icon.Close width={15} height={15} />
                        </button>
                    </header>

                    <div className="wa-scroll min-h-0 flex-1 overflow-y-auto px-5 py-5">
                        <div className="flex flex-col items-center pb-5 text-center">
                            <Avatar initials={active.initials} accent={sourceColour(active.source) || active.accent} size={80} ring />
                            <div className="mt-3 text-[17px] font-bold text-wa-text-strong">
                                {profile.name}
                            </div>
                            <div className="mt-1 text-[12.5px] tabular-nums text-wa-sub">{profile.phone}</div>

                            {profile.zohoId ? (
                                <a
                                    href={profile.zohoId}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="zoho-link"
                                    className="wa-btn mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold"
                                    style={{ background: "var(--wa-accent-soft)", color: "var(--wa-accent)" }}
                                >
                                    <Icon.Lead width={12} height={12} />
                                    Zoho Lead
                                    <Icon.External width={11} height={11} />
                                </a>
                            ) : (
                                <span
                                    data-testid="zoho-link"
                                    className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold text-wa-sub"
                                    style={{ background: "var(--wa-divider)" }}
                                >
                                    <Icon.NoLead width={12} height={12} />
                                    No Zoho lead
                                </span>
                            )}
                        </div>

                        {/* Lead source — coloured chip matching the avatar colour. */}
                        {profile.source && (
                            <div className="mb-3 flex items-center justify-center">
                                <span
                                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-semibold"
                                    style={{
                                        background: sourceColour(profile.source) ? `${sourceColour(profile.source)}22` : "var(--wa-panel2)",
                                        color: sourceColour(profile.source) || "var(--wa-text-strong)",
                                    }}
                                >
                                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: sourceColour(profile.source) || "var(--wa-sub)" }} />
                                    {profile.source}
                                </span>
                            </div>
                        )}

                        <div
                            className="rounded-2xl p-1"
                            style={{ background: "var(--wa-panel2)" }}
                        >
                            {[
                                ["Source", profile.source],
                                ["First name", profile.firstName],
                                ["Last name", profile.lastName],
                                ["Email", profile.email],
                                ["Budget", profile.budget],
                                ["Area", profile.area],
                                ["Move-in", profile.moveIn],
                                ["Occupancy", profile.occupancy],
                                ["Stay length", profile.stay],
                            ].map(([k, v], i, arr) => (
                                <div
                                    key={k}
                                    className="flex items-center justify-between gap-3 px-3 py-3"
                                    style={{
                                        borderBottom:
                                            i < arr.length - 1 ? "1px solid var(--wa-divider)" : "none",
                                    }}
                                >
                                    <span className="text-[12px] uppercase tracking-wide text-wa-sub">
                                        {k}
                                    </span>
                                    <span className="text-right text-[13.5px] font-medium text-wa-text-strong">
                                        {v || "—"}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5">
                            <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-wa-sub">
                                Conversation state
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between rounded-xl px-3 py-2.5"
                                     style={{ background: "var(--wa-panel2)" }}>
                                    <span className="flex items-center gap-2 text-[13px] text-wa-text">
                                        <Icon.Headset width={14} height={14} className="text-wa-sub" />
                                        Assigned staff
                                    </span>
                                    <span className="text-[13px] font-semibold text-wa-text-strong">
                                        {assignedStaff?.name || active.assignedName || "Unassigned"}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between rounded-xl px-3 py-2.5"
                                     style={{ background: "var(--wa-panel2)" }}>
                                    <span className="flex items-center gap-2 text-[13px] text-wa-text">
                                        <Icon.Spark width={14} height={14} className="text-wa-sub" />
                                        Takeover
                                    </span>
                                    <span className={cx(
                                        "text-[12px] font-bold uppercase tracking-wide",
                                    )}
                                          style={{ color: humanTakeover ? "var(--wa-danger)" : "var(--wa-sub)" }}
                                    >
                                        {humanTakeover ? "Active" : "Off"}
                                    </span>
                                </div>

                                {/* LEAD ACTIVITY + manual override.
                                    Three states, so a three-way segmented control rather
                                    than a switch: Auto is a genuinely different thing from
                                    "pinned active", and a two-way control cannot express
                                    "hand back to the 7-day rule". The header line shows the
                                    EFFECTIVE state (what the sidebar filter uses), the
                                    segments show WHICH rule is in force, and the caption
                                    spells out the consequence. */}
                                <div className="rounded-xl px-3 py-2.5" style={{ background: "var(--wa-panel2)" }}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="flex items-center gap-2 text-[13px] text-wa-text">
                                            <span
                                                className="inline-block h-2 w-2 rounded-full"
                                                style={{
                                                    background: active.activeLead === true ? "var(--wa-accent)"
                                                        : active.activeLead === false ? "var(--wa-danger)"
                                                        : "var(--wa-sub)",
                                                }}
                                            />
                                            Lead activity
                                        </span>
                                        <span
                                            data-testid="profile-activity-state"
                                            className="text-[12px] font-bold uppercase tracking-wide"
                                            style={{
                                                color: active.activeLead === true ? "var(--wa-accent)"
                                                    : active.activeLead === false ? "var(--wa-danger)"
                                                    : "var(--wa-sub)",
                                            }}
                                        >
                                            {active.activeLead === true ? "Active"
                                                : active.activeLead === false ? "Inactive"
                                                : "Unknown"}
                                        </span>
                                    </div>

                                    <div
                                        data-testid="profile-activity-override"
                                        role="group"
                                        aria-label="Set lead activity"
                                        className="mt-2 flex items-center gap-1 rounded-full p-[3px]"
                                        /* recessed track: --wa-bg sits below the card in both
                                           themes, and leaves .wa-btn:hover (--wa-panel3) visible
                                           on the unselected segments. */
                                        style={{ background: "var(--wa-bg)" }}
                                    >
                                        {[
                                            { key: null, label: "Auto" },
                                            { key: "active", label: "Active" },
                                            { key: "inactive", label: "Inactive" },
                                        ].map((opt) => {
                                            const on = (active.activityOverride || null) === opt.key;
                                            const tint = opt.key === "inactive" ? "var(--wa-danger)" : "var(--wa-accent)";
                                            return (
                                                <button
                                                    key={opt.label}
                                                    type="button"
                                                    data-testid={`activity-set-${opt.key || "auto"}`}
                                                    aria-pressed={on}
                                                    disabled={savingActivity || blockedByTestMode || active.activityOverrideReady === false}
                                                    onClick={() => {
                                                        if (on || savingActivity) return;
                                                        setSavingActivity(true);
                                                        Promise.resolve(setLeadActivity(active.phone, opt.key))
                                                            .catch(() => {})
                                                            .finally(() => setSavingActivity(false));
                                                    }}
                                                    className={cx(
                                                        "wa-btn flex-1 rounded-full py-1.5 text-[12px]",
                                                        on ? "font-semibold" : "font-medium text-wa-sub",
                                                        "disabled:cursor-not-allowed disabled:opacity-50",
                                                    )}
                                                    style={on
                                                        ? { background: opt.key === "inactive" ? "var(--wa-danger-soft)" : "var(--wa-accent-soft)", color: tint }
                                                        : undefined}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className="mt-1.5 text-[11px] leading-snug text-wa-sub">
                                        {active.activityOverrideReady === false
                                            ? "Manual override unavailable — the users.activity_override column isn't in the database yet."
                                            : active.activityIsManual
                                                ? "Set by hand. A new message from this lead clears it back to Auto."
                                                : "Automatic — active for 7 days after their last inbound message."}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            )}
              </>
            ) : (
                <main
                    data-testid="chat-empty"
                    className="wa-wallpaper flex h-full min-w-0 flex-1 flex-col items-center justify-center"
                    style={{ background: "var(--wa-bg)" }}
                >
                    <div className="max-w-sm px-8 text-center">
                        <div
                            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
                            style={{ background: "var(--wa-panel)", color: "var(--wa-sub)", boxShadow: "var(--wa-shadow)" }}
                        >
                            <Icon.Robot width={28} height={28} />
                        </div>
                        <div className="text-[17px] font-bold text-wa-text-strong">Figment Inbox</div>
                        <p className="mt-1.5 text-[13.5px] leading-relaxed text-wa-sub">
                            Click on any conversation on the left to view the chat and take over from the AI when needed.
                        </p>
                    </div>
                </main>
            )}

            {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

            {showStaff && (
                <StaffManager
                    staffAll={STAFF_ALL}
                    onClose={() => setShowStaff(false)}
                    onBulkReassign={bulkReassign}
                    onReassignAll={reassignAll}
                    onRedistribute={redistribute}
                    onSetActive={setActive}
                />
            )}
        </div>
    );
}

/* ----------------------------- AUTH GATE --------------------- */
// Google sign-in gate (Google Identity Services). Renders the inbox only after a
// verified @figment.live account signs in (remembered for the browser session via
// sessionStorage). The signed-in email is passed to <Inbox> as `signedInEmail` so
// any human reply is attributed (stamped as staff_email by the Worker). If no
// Client ID is configured the gate is skipped (dev convenience).
const AUTH_KEY = "inbox_auth";

export default function App() {
    const [auth, setAuth] = useState(() => {
        try { const s = sessionStorage.getItem(AUTH_KEY); return s ? JSON.parse(s) : null; }
        catch { return null; }
    });
    const [err, setErr] = useState("");
    const btnRef = useRef(null);

    // Apply the persisted theme to <html> on the login screen too, so the gate is
    // on-brand (the inbox manages it once mounted).
    useEffect(() => {
        const root = document.documentElement;
        if (!root.classList.contains("dark") && !root.classList.contains("light")) {
            const t = (() => { try { return localStorage.getItem("wa-theme"); } catch { return null; } })() || "dark";
            root.classList.add(t);
        }
    }, []);

    useEffect(() => {
        if (auth || !GOOGLE_CLIENT_ID) return;
        const handleCredential = (resp) => {
            try {
                const claims = JSON.parse(
                    atob(resp.credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
                );
                if (claims.email_verified && claims.hd === ALLOWED_DOMAIN) {
                    const a = { email: claims.email, name: claims.name || claims.email };
                    try { sessionStorage.setItem(AUTH_KEY, JSON.stringify(a)); } catch { /* ignore */ }
                    setAuth(a); setErr("");
                } else {
                    setErr("Access is restricted to @" + ALLOWED_DOMAIN + " accounts.");
                }
            } catch { setErr("Sign-in failed, please try again."); }
        };

        // Ensure the GSI client script is present, then init + render the button.
        const ensureScript = () => {
            if (document.getElementById("google-gsi")) return;
            const s = document.createElement("script");
            s.src = "https://accounts.google.com/gsi/client";
            s.async = true;
            s.defer = true;
            s.id = "google-gsi";
            document.head.appendChild(s);
        };
        ensureScript();

        let timer;
        const init = () => {
            if (!window.google?.accounts?.id) return false;
            window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleCredential });
            if (btnRef.current) {
                window.google.accounts.id.renderButton(btnRef.current, {
                    theme: "filled_blue", size: "large", text: "signin_with", shape: "pill",
                });
            }
            return true;
        };
        if (!init()) { timer = setInterval(() => { if (init()) clearInterval(timer); }, 200); }
        return () => clearInterval(timer);
    }, [auth]);

    if (auth || !GOOGLE_CLIENT_ID) {
        return <Inbox signedInEmail={auth ? auth.email : ""} />;
    }

    return (
        <div
            data-testid="login-gate"
            className="flex h-screen w-full items-center justify-center bg-wa-bg text-wa-text"
        >
            <div
                className="w-[360px] rounded-2xl p-7 text-center"
                style={{
                    background: "var(--wa-panel)",
                    border: "1px solid var(--wa-divider)",
                    boxShadow: "var(--wa-shadow)",
                }}
            >
                <div
                    className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ background: "var(--wa-accent-soft)", color: "var(--wa-accent)" }}
                >
                    <Icon.Robot width={22} height={22} />
                </div>
                <h1 className="text-[18px] font-bold tracking-tight text-wa-text-strong">
                    Figment Inbox
                </h1>
                <p className="mb-6 mt-1.5 text-[13px] text-wa-sub">
                    Sign in with your <b className="text-wa-text-strong">@figment.live</b> account to continue.
                </p>
                <div ref={btnRef} className="flex justify-center" />
                {err && <p className="mt-4 text-[12.5px]" style={{ color: "var(--wa-danger)" }}>{err}</p>}
            </div>
        </div>
    );
}
